// Utils
import { isNumber as _isNumber } from 'lodash-es';

// Constants
const spacingStandard = {
  '0': '0',
  1: '4px',
  px: '4px',
};

type AssignedMultipliers = {
  top?: number;
  left?: number;
  bottom?: number;
  right?: number;
};

type IndexedMultipliers = [number, number, number?, number?];

export type Spacing =
  | undefined
  | AssignedMultipliers
  | number
  | IndexedMultipliers;

type ReturnType =
  | {
      margin: number | string;
    }
  | Record<string, unknown>;

const SCALE = parseInt(spacingStandard.px, 10);

const positionMap: Record<keyof AssignedMultipliers, string> = {
  top: 'marginTop',
  bottom: 'marginBottom',
  left: 'marginLeft',
  right: 'marginRight',
};

const ordinalMapByLength: Record<number, number[]> = {
  2: [0, 1],
  3: [0, 1, 2, 1],
  4: [0, 1, 2, 3],
};

const generateMarginStyles = (
  length: number,
  spacing: number[],
): { margin: string } => {
  let margins = '';
  const ordinalMap = ordinalMapByLength[length];
  if (!ordinalMap) {
    return { margin: '' };
  }

  ordinalMap.forEach((index) => {
    margins += `${spacing[index]}px `;
  });
  return { margin: margins.trim() };
};

export const useSpacing = (spacing: Spacing): ReturnType => {
  if (!spacing) {
    return {};
  }

  if (Array.isArray(spacing)) {
    const scaledSpacing = spacing
      .filter(_isNumber)
      .map((scale) => scale * SCALE);

    return generateMarginStyles(spacing.length, scaledSpacing);
  }

  if (spacing.constructor.name === 'Object') {
    const multipliers = spacing as AssignedMultipliers;

    const positionStyles: Record<string, number> = {};
    Object.entries(positionMap).forEach(([position, marginKey]) => {
      const multiplierPosition = position as keyof AssignedMultipliers;
      const multiplier = multipliers[multiplierPosition];
      if (_isNumber(multiplier)) {
        positionStyles[marginKey] = multiplier * SCALE;
      }
    });

    return positionStyles;
  }

  return { margin: `${Number(spacing) * SCALE}px` };
};
